// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace Scheduler {
  /**
   * Schedules a function `f` using a `setTimeout` after a `timeout` ms.
   */
  export const timeout =
    (timeout?: number): Scheduler =>
    f => {
      const handle = setTimeout(f, timeout)
      return () => clearTimeout(handle)
    }

  /**
   * Schedules a function `f` using a `requestAnimationFrame`. Works only in web browsers.
   */
  export const frame = (): Scheduler => f => {
    const handle = requestAnimationFrame(f)
    return () => cancelAnimationFrame(handle)
  }

  /**
   * Unschedule a scheduled call by calling this function.
   */
  export type Unschedule = () => void

  /**
   * Schedules a function `f` call and returns an unschedule function.
   */
  export type Scheduler = (f: () => void) => Unschedule
}

export interface ProcessControllerOptions {
  /**
   * How much to wait until scheduling the next iteration. Default is 30 ms since iteration start.
   */
  maxIterationTime?: number

  /**
   * How to schedule a next iteration. Available ones are `Scheduler.timeout` (default) and `Scheduler.frame`.
   */
  scheduler?: Scheduler.Scheduler
}

export interface ControlledProcess {
  /**
   * Checks if a current iteration must be rescheduled and reschedules if needed.
   *
   * @throws ControlledProcessStopped An exception signalling that a process was stopped midway. Capture it to stop your process midway. Should be captured only at the top-level of your async process.
   */
  checkpoint(): Promise<void>
}

export interface ProcessController {
  /**
   * Stops the process before the next checkpoint.
   */
  stop(): void
}

/**
 * Creates a process controller and a controlled process.
 *
 * @example
 * async function fetchAndProcess(process: ProcessController): Promise<Result[]> {
 *   try {
 *     const entities = await fetchEntities()
 *     for (const entity of entities) {
 *       // If a current iteration takes more than `maxIterationTime`,
 *       // then reschedules the next iteration using a `scheduler`.
 *       await process.checkpoint()
 *       await processEntity(entity)
 *     }
 *   } catch (e) {
 *     // If a process is stopped after scheduling the next iteration,
 *     // `checkpoint` throws an exception that must be captured at the
 *     // top-level async function of your process.
 *     // (Yes, I know that using exceptions for control flow is an antipattern.)
 *     if (!(e instanceof ControlledProcessStopped)) {
 *       throw e
 *     }
 *   }
 * }
 *
 * // Create a controller
 * const [controller, process] = processController()
 * // Start a long-running process but don't wait until it ends
 * void fetchAndProcess(process)
 * // Some time later when a process is not needed anymore, or should be restarted
 * controller.stop()
 */
export const processController = (
  opts?: ProcessControllerOptions
): [ProcessController, ControlledProcess] => {
  const x = ProcessControllerImpl.create(opts)
  return [x, x]
}

class ProcessControllerImpl implements ProcessController, ControlledProcess {
  static create(opts?: ProcessControllerOptions): ProcessControllerImpl {
    return new ProcessControllerImpl(opts)
  }

  private readonly maxIterationTime: number
  private readonly scheduler: Scheduler.Scheduler

  private unschedule: Scheduler.Unschedule | undefined
  private iterationStart: number
  private stopped: boolean

  private constructor(opts?: ProcessControllerOptions) {
    this.maxIterationTime = opts?.maxIterationTime ?? 30
    this.scheduler = opts?.scheduler ?? Scheduler.timeout()
    this.unschedule = undefined
    this.iterationStart = Number.NEGATIVE_INFINITY
    this.stopped = false
  }

  stop(): void {
    this.unschedule?.()
    this.stopped = true
  }

  async checkpoint(): Promise<void> {
    if (this.stopped) {
      throw new ControlledProcessStopped()
    }
    if (this.shouldReschedule()) {
      await this.reschedule()
      this.iterationStarted()
    }
  }

  private reschedule(): Promise<void> {
    return new Promise(resolve => {
      this.unschedule = this.scheduler(() => {
        this.unschedule = undefined
        resolve()
      })
    })
  }

  private shouldReschedule(): boolean {
    return Date.now() - this.iterationStart > this.maxIterationTime
  }

  private iterationStarted(): void {
    this.iterationStart = Date.now()
  }
}

export class ControlledProcessStopped extends Error {}
